import { Box, Paper } from '@mui/material'
import { GridColumns, GridRowId, GridRowParams } from '@mui/x-data-grid-pro'
import { FC, memo, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Benchmark, CollectionSpace, FieldQueryOptions } from '@cibo/core'
import {
  FieldSearchBar,
  PaginatedFieldsContext,
  ReadOnlyFieldsTableProps,
  SelectableFieldsTableProps,
  SelectablePaginatedFieldsTable,
  fieldAcresColumn,
  fieldCreatedAtColumn,
  fieldLocationColumn,
  fieldNameLocationColumn,
  mutateSortModelForLocation,
} from '@cibo/landmanager'
import { useFigures } from '@cibo/ui'

import { usePaginatedWorkflowFields, useProgram } from '../../queries'
import { BenchmarkStatusModalStateDialogActions } from '../BenchmarkStatus'
import { useProgramFieldColumns } from '../ProgramFieldColumns'

interface BaseWorkflowFieldsTableProps {
  programId: string
  space?: CollectionSpace
  variant: 'create' | 'proceed'
  workflowId?: string
  benchmark: Benchmark
  baseQuery?: FieldQueryOptions
}

interface WorkflowFieldsTableProps extends BaseWorkflowFieldsTableProps {
  Component: FC<any>
  componentProps?: SelectableFieldsTableProps | ReadOnlyFieldsTableProps
  ReasonDialogActions?: BenchmarkStatusModalStateDialogActions
}

export const WorkflowFieldsTable = ({
  benchmark,
  Component,
  componentProps,
  programId,
  space,
  variant,
  workflowId,
  baseQuery,
  ReasonDialogActions,
}: WorkflowFieldsTableProps) => {
  const { t } = useTranslation('@cibo/programs/components/SelectWorkflowFieldsTable')
  const { t: tColumn } = useTranslation('@cibo/landmanager/FieldsTable')
  const { filters } = useContext(PaginatedFieldsContext)
  const { acres } = useFigures()
  const { data: program } = useProgram({ programId })

  const {
    fieldProgramDollarOpportunityColumn,
    fieldProgramCreditOpportunityColumn,
    fieldProgramRecommendationColumn,
  } = useProgramFieldColumns(programId)

  const columns = useMemo(() => {
    const columns = [
      fieldNameLocationColumn({ t: tColumn }),
      fieldProgramRecommendationColumn({ benchmark, DialogActions: ReasonDialogActions }),
    ] as GridColumns<any>

    if (!!program) {
      if (
        program.opportunitiesByWfStatus?.notInWorkflow.find(o =>
          o.reward.find(r => r.token === 'carbonCredit')
        )
      ) {
        columns.push(fieldProgramCreditOpportunityColumn)
      }
      if (
        program.opportunitiesByWfStatus?.notInWorkflow.find(o =>
          o.reward.find(r => r.token === 'usd')
        )
      ) {
        columns.push(fieldProgramDollarOpportunityColumn)
      }
    }

    columns.push(fieldLocationColumn({ t }), fieldAcresColumn({ acres, t }))
    variant === 'create' && columns.push(fieldCreatedAtColumn({ t }))
    return columns
  }, [programId, filters, variant, program])

  return (
    <Paper elevation={0}>
      <Component
        columns={columns}
        initialSort={[{ field: 'recommendationScore', sort: 'desc' }]}
        mutateSortModel={mutateSortModelForLocation}
        baseQuery={{
          ...filters,
          programs: [
            {
              id: programId,
              benchmarks: [{ benchmark }],
            },
          ],
          space,
          workflow: workflowId,
          ...baseQuery,
        }}
        components={{
          Toolbar: memo(() => (
            <Box m={2}>
              <FieldSearchBar />
            </Box>
          )),
        }}
        isRowSelectable={(params: GridRowParams) => {
          if (variant === 'proceed') {
            return !!(workflowId && params.row.workflowStatus(workflowId, benchmark) === 'pass')
          }
          return (
            !params.row.hasLedgerConfict(programId) &&
            !params.row.isParticipatingInProgram(programId)
          )
        }}
        {...componentProps}
      />
    </Paper>
  )
}

export const SelectFieldsFromWorkflowTable = (
  props: Omit<BaseWorkflowFieldsTableProps, 'workflowId'> &
    Required<Pick<BaseWorkflowFieldsTableProps, 'workflowId'>> &
    Pick<WorkflowFieldsTableProps, 'ReasonDialogActions'>
) => {
  const { workflowId } = props
  const { setSelectedRowIds } = useContext(PaginatedFieldsContext)
  const { isLoading, data: workflowFields } = usePaginatedWorkflowFields(workflowId)
  useEffect(() => {
    if (!isLoading && !!workflowFields) {
      const eligibleWorkflowIds = workflowFields.items
        ?.map(field => {
          return field.workflowStatus(workflowId, props.benchmark) === 'pass' ? field.id : undefined
        })
        .filter(item => item !== undefined)
      setSelectedRowIds(eligibleWorkflowIds as GridRowId[])
    }
  }, [props.workflowId, isLoading])
  return <WorkflowFieldsTable Component={SelectablePaginatedFieldsTable} {...props} />
}
